/**
 * `diagram/edgeGeometry.ts` — pure geometry for anchoring an edge to a node's border.
 *
 * The bug this fixes: with fixed top/bottom (or left/right) handles, an edge always left one
 * node's bottom-center and arrived at the other's top-center regardless of where the two tiles
 * actually sat — so endpoints landed at seemingly random spots, floating off the shape. Instead
 * we compute, for each endpoint, the point where the straight line between the two tile *centers*
 * crosses that tile's bounding box — so an arrow always terminates **on the shape's border,
 * facing the other node** (the standard "floating edge" technique; how Lucid/draw.io behave).
 *
 * No React, no DOM — the same functions drive the live canvas edge (`FloatingEdge.tsx`) and the
 * positioned SVG export (`exportSvg.ts`), so on-screen and exported arrows match.
 */

/** A side of a node's bounding box — mirrors React Flow's `Position` names, DOM-free. */
export type BoxSide = 'top' | 'right' | 'bottom' | 'left';

/** An axis-aligned box given by its center + size. */
export interface Box {
  cx: number;
  cy: number;
  w: number;
  h: number;
}

/**
 * The point on `box`'s boundary hit by the ray from its center toward `(towardX, towardY)`.
 * Returns the center itself when the target coincides with the center (degenerate — caller
 * decides what to draw). Robust for any aspect ratio: we scale the direction vector until it
 * touches the nearer of the vertical/horizontal edges.
 */
export function boxEdgePoint(box: Box, towardX: number, towardY: number): { x: number; y: number } {
  const dx = towardX - box.cx;
  const dy = towardY - box.cy;
  if (dx === 0 && dy === 0) return { x: box.cx, y: box.cy };
  const hw = box.w / 2;
  const hh = box.h / 2;
  // How far along the ray until we hit the vertical edge (|dx|·t = hw) vs. the horizontal edge.
  const tx = dx !== 0 ? hw / Math.abs(dx) : Infinity;
  const ty = dy !== 0 ? hh / Math.abs(dy) : Infinity;
  const t = Math.min(tx, ty);
  return { x: box.cx + dx * t, y: box.cy + dy * t };
}

/**
 * Which side of `box` a boundary point sits on (used to orient the bezier's control handles).
 * Compares the point against the box edges with a 1px tolerance; ties resolve to the axis the
 * point is closest to an edge on.
 */
export function boxSide(box: Box, px: number, py: number): BoxSide {
  const left = box.cx - box.w / 2;
  const right = box.cx + box.w / 2;
  const top = box.cy - box.h / 2;
  const bottom = box.cy + box.h / 2;
  // Distance to each edge; smallest wins (the point is on exactly one edge, modulo rounding).
  const dl = Math.abs(px - left);
  const dr = Math.abs(px - right);
  const dt = Math.abs(py - top);
  const db = Math.abs(py - bottom);
  const min = Math.min(dl, dr, dt, db);
  if (min === dt) return 'top';
  if (min === db) return 'bottom';
  if (min === dl) return 'left';
  return 'right';
}

/** Both endpoints + their sides for an edge between two boxes (centers drive the direction). */
export function edgeAnchors(
  source: Box,
  target: Box,
): { sx: number; sy: number; tx: number; ty: number; sourceSide: BoxSide; targetSide: BoxSide } {
  const sp = boxEdgePoint(source, target.cx, target.cy);
  const tp = boxEdgePoint(target, source.cx, source.cy);
  return {
    sx: sp.x,
    sy: sp.y,
    tx: tp.x,
    ty: tp.y,
    sourceSide: boxSide(source, sp.x, sp.y),
    targetSide: boxSide(target, tp.x, tp.y),
  };
}
